import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Button } from "antd";
import { useLocation, Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { verifyOTPAction } from "@redux/actions/authActions";
import { resendOTP } from "@api/auth/auth";
import { SuccessNotification, ErrorNotification } from "@components";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";
import LeftContent from "../signup/leftContent";
import { Helmet } from "react-helmet-async";

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const search = useLocation().search;
  const emailFromUrl = new URLSearchParams(search).get("email");

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiryTime, setExpiryTime] = useState(600); // 10 minutes in seconds
  const inputRefs = useRef([]);

  // If no email, redirect to signup
  useEffect(() => {
    if (!emailFromUrl) {
      history.push("/signup");
    }
  }, [emailFromUrl, history]);

  // Countdown for expiry timer
  useEffect(() => {
    if (expiryTime <= 0) return;

    const timer = setInterval(() => {
      setExpiryTime((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [expiryTime]);

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle OTP input change
  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5 && newOtp.every((digit) => digit !== "")) {
      handleVerify(newOtp.join(""));
    }
  };

  // Handle key down (backspace navigation)
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      setError("");
      inputRefs.current[5]?.focus();
      handleVerify(pastedData);
    }
  };

  // Handle verify
  const handleVerify = (otpCode) => {
    const code = otpCode || otp.join("");
    if (code.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    dispatch(
      verifyOTPAction({
        email: emailFromUrl,
        otp: code,
        history,
        setLoading,
        setError,
      })
    );
  };

  // Handle resend OTP
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    try {
      const res = await resendOTP(emailFromUrl);
      SuccessNotification(res.data.message);
      setResendCooldown(60);
      setExpiryTime(600); // Reset expiry timer
      setOtp(["", "", "", "", "", ""]);
      setError("");
      inputRefs.current[0]?.focus();
    } catch (err) {
      const errorMessage = err.response?.data?.type?.[0]?.message;
      if (err.response?.data?.retryAfter) {
        setResendCooldown(err.response.data.retryAfter);
      }
      ErrorNotification(errorMessage || "Failed to resend code");
    }
  };

  return (
    <>
      <Helmet>
        <title>Verify Email | Copilotly - Your Personal AI Copilot</title>
        <meta name="description" content="Verify your email to complete your Copilotly account setup." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Row
        gutter={[32, 0]}
        className="hp-authentication-page"
        style={{ overflowY: "auto", height: "100vh" }}
      >
        <LeftContent />

        <Col
          lg={8}
          md={16}
          sm={16}
          span={24}
          className="hp-py-sm-0 hp-py-md-0"
          justify="center"
        >
          <Row className="hp-h-100" align="middle" justify="center">
            <Col
              xxl={11}
              xl={15}
              lg={20}
              md={16}
              sm={24}
              className="hp-px-sm-8"
              style={{ paddingTop: "12px", paddingBottom: "16px" }}
            >
              <img
                src={logoText}
                alt="logo"
                style={{
                  maxWidth: "140px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto 8px",
                }}
              />
              <h2
                className="hp-mb-sm-0"
                style={{
                  textAlign: "center",
                  fontSize: "18px",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.85)",
                  marginBottom: "8px",
                }}
              >
                Verify Your Email
              </h2>

              <p
                style={{
                  textAlign: "center",
                  color: "#666",
                  fontSize: "13px",
                  marginBottom: "16px",
                }}
              >
                We sent a 6-digit code to<br />
                <strong>{emailFromUrl}</strong>
              </p>

              {/* OTP Input */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    style={{
                      width: "42px",
                      height: "48px",
                      fontSize: "20px",
                      fontWeight: "600",
                      textAlign: "center",
                      border: error ? "1px solid #ff4d4f" : "1px solid #d9d9d9",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#1890ff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = error ? "#ff4d4f" : "#d9d9d9";
                    }}
                  />
                ))}
              </div>

              {/* Error message */}
              {error && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginBottom: "8px",
                  }}
                >
                  {error}
                </p>
              )}

              {/* Expiry timer */}
              <p
                style={{
                  textAlign: "center",
                  color: expiryTime < 60 ? "#ff4d4f" : "#666",
                  fontSize: "12px",
                  marginBottom: "12px",
                }}
              >
                {expiryTime > 0 ? (
                  <>Code expires in {formatTime(expiryTime)}</>
                ) : (
                  <>Code expired. Please request a new one.</>
                )}
              </p>

              {/* Verify button */}
              <Button
                block
                type="primary"
                onClick={() => handleVerify()}
                loading={loading}
                disabled={otp.some((digit) => digit === "")}
                style={{ height: "36px", fontSize: "13px", marginBottom: "12px" }}
              >
                Verify Email
              </Button>

              {/* Resend code */}
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <span style={{ fontSize: "13px", color: "#666" }}>
                  Didn't receive the code?{" "}
                </span>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  style={{
                    background: "none",
                    border: "none",
                    color: resendCooldown > 0 ? "#999" : "#1890ff",
                    cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    padding: 0,
                  }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>

              <div className="login-create-acc" style={{ marginBottom: "6px", fontSize: "13px" }}>
                <p style={{ margin: 0 }}>Wrong email?</p>
                <Link to="/signup" style={{ marginLeft: "4px" }}> Sign up again</Link>
              </div>

              <Col
                className="hp-other-links"
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: "12px",
                }}
              >
                <Link
                  to="/privacy"
                  className="hp-text-color-black-80 hp-text-color-dark-40"
                  style={{
                    marginRight: "8px",
                    fontSize: "11px",
                  }}
                >
                  Privacy Policy
                </Link>

                <span
                  className="hp-text-color-black-80 hp-text-color-dark-40"
                  style={{ margin: "0 6px", fontSize: "11px" }}
                >
                  ·
                </span>

                <Link
                  to="/terms"
                  className="hp-text-color-black-80 hp-text-color-dark-40"
                  style={{ fontSize: "11px" }}
                >
                  Terms of Service
                </Link>
              </Col>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default VerifyEmail;
